import React from "react";
import CustomSelect from "../../components/CustomSelect";
import { validateStrongPassword } from "../../Functions/firebaseFunction";

const FormField = ({ field, value, onChange, error }) => (
  <div className="flex flex-col">
    <label className="font-semibold text-gray-700 mb-2">{field.label}</label>
    <input
      type={field.type}
      value={value}
      onChange={(e) => onChange(field.key, e.target.value)}
      placeholder={field.placeholder}
      className="border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
      required
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const StaffForm = ({
  state,
  formData,
  setFormData,
  updateState,
  clearFormData,
  locationId, // INJECTED FROM MODAL
  addStaffMember,
  updateStaffMember,
  currentUser,
  hasManager,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleInputChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid || isSubmitting) return;

    setIsSubmitting(true);
    const isManager = formData.selectedRole === "Manager";

    if (isManager && (!state.isEditing || formData.password)) {
      const passwordError = validateStrongPassword(formData.password);
      if (passwordError) {
        alert(passwordError);
        setIsSubmitting(false);
        return;
      }
    }
    
    let generatedPin = "";
    if (!isManager && formData.dateOfBirth) {
       // e.g. YYYY-MM-DD
       const namePart = (formData.firstName || "").substring(0, 4).toUpperCase();
       const parts = formData.dateOfBirth.split("-");
       if (parts.length === 3) {
          generatedPin = `${namePart}${parts[1]}${parts[2]}`;
       }
    }

    const staffData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      mobileNo: formData.mobileNo,
      countryCode: formData.countryCode || "+1",
      role: formData.selectedRole,
      address: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`,
      storeLocation: locationId, // Use the provided locationId
    };

    if (isManager) {
      staffData.email = formData.email;
      if (!state.isEditing || formData.password) {
        staffData.password = formData.password;
      }
    } else {
      staffData.dateOfBirth = formData.dateOfBirth;
      staffData.pinCode = generatedPin || formData.pinCode;
    }
    try {
      if (state.isEditing) {
        await updateStaffMember(currentUser.uid, state.editingId, staffData);
      } else {
        await addStaffMember(currentUser.uid, staffData);
      }
      clearFormData();
      updateState({ showPopup: false });
    } catch (err) {
      console.error("Staff submit error:", err);
      alert("Error: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formFields = [
    {
      key: "firstName",
      label: "First Name",
      type: "text",
      placeholder: "First Name",
    },
    {
      key: "lastName",
      label: "Last Name",
      type: "text",
      placeholder: "Last Name",
    },
    ...(formData.selectedRole === "Manager" ? [
      {
        key: "email",
        label: "Email",
        type: "email",
        placeholder: "Email Address",
      },
      {
        key: "password",
        label: "Password",
        type: "password",
        placeholder: state.isEditing ? "Leave blank to keep current" : "8+ chars, 1 uppercase, 1 lowercase, 1 number, 1 special char",
      }
    ] : [
      {
        key: "dateOfBirth",
        label: "Date of Birth",
        type: "date",
        placeholder: "DOB (used for login PIN)",
      }
    ]),
    {
      key: "countryCode",
      label: "Country Code",
      type: "text",
      placeholder: "e.g. +1",
    },
    {
      key: "mobileNo",
      label: "Mobile No.",
      type: "text",
      placeholder: "e.g., 9876543210",
    },
    // selectedRole removed as it is now strictly controlled by the button clicked in BranchStaffModal
    // storeLocation removed
    {
      key: "address",
      label: "Address",
      type: "text",
      placeholder: "Street Address",
    },
    {
      key: "city",
      label: "City",
      type: "text",
      placeholder: "City",
    },
    {
      key: "state",
      label: "State",
      type: "text",
      placeholder: "State",
    },
    {
      key: "pincode",
      label: "Pincode",
      type: "text",
      placeholder: "Pincode",
    },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {state.isEditing ? "Edit Staff" : "Add New Staff"}
          </h2>
          <button
            onClick={() => {
              if (isSubmitting) return;
              clearFormData();
              updateState({ showPopup: false });
            }}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {formFields.map((field) =>
              field.type === "select" ? (
                <CustomSelect
                  key={field.key}
                  label={field.label}
                  options={field.options}
                  value={formData[field.key]}
                  onChange={(value) => handleInputChange(field.key, value)}
                  disabled={isSubmitting}
                />
              ) : (
                <FormField
                  key={field.key}
                  field={field}
                  value={formData[field.key]}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              )
            )}
          </div>

          <div className="flex space-x-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                clearFormData();
                updateState({ showPopup: false });
              }}
              className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 py-3 rounded-lg text-white font-semibold transition-colors flex justify-center items-center ${
                isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? "Processing..." : (state.isEditing ? "Update Staff" : "Add Staff")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffForm;
